import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock, BookOpen, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const Courses = () => {
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('masters');

  const mastersCourses = [
    {
      id: 'm1',
      title: 'Foundations of Humanitarian Action',
      duration: '1 Month',
      description: 'Explore the historical evolution, key actors, and fundamental concepts that underpin humanitarian action today.',
      topics: [
        'History of humanitarianism and its evolution',
        'Key actors in the humanitarian system (UN, NGOs)',
        'Humanitarian architecture and coordination mechanisms',
        'Contemporary challenges and trends in humanitarian response',
        'Introduction to humanitarian standards (Sphere, CHS)'
      ]
    },
    {
      id: 'm2',
      title: 'Humanitarian Principles & International Law',
      duration: '1 Month',
      description: 'Understand the legal and ethical frameworks that guide humanitarian work in conflict and disaster settings.',
      topics: [
        'Core humanitarian principles',
        'International Humanitarian Law (IHL) fundamentals',
        'International Human Rights Law and Refugee Law',
        'Protection of civilians in armed conflict',
        'Ethical dilemmas and decision-making in humanitarian contexts'
      ]
    },
    {
      id: 'm3',
      title: 'Program & Project Management in NGOs',
      duration: '1 Month',
      description: 'Master the tools and techniques for effective humanitarian program design, implementation, and management.',
      topics: [
        'Project cycle management in humanitarian contexts',
        'Needs assessment and program design',
        'Logical frameworks and theory of change',
        'Budget development and financial management',
        'Risk management and adaptive programming'
      ]
    },
    {
      id: 'm4',
      title: 'Monitoring, Evaluation, Accountability & Learning (MEAL)',
      duration: '1 Month',
      description: 'Develop robust systems for measuring impact, ensuring accountability, and driving organizational learning.',
      topics: [
        'MEAL frameworks and indicator development',
        'Data collection methods and tools',
        'Accountability to affected populations (AAP)',
        'Community feedback mechanisms',
        'Learning and knowledge management'
      ]
    },
    {
      id: 'm5',
      title: 'Emergency Response & Disaster Management',
      duration: '1 Month',
      description: 'Learn to plan for, respond to, and recover from natural disasters and complex emergencies.',
      topics: [
        'Disaster risk reduction and preparedness',
        'Emergency response mechanisms and rapid assessment',
        'Camp coordination and management',
        'Search and rescue operations overview',
        'Early recovery and transition programming'
      ]
    },
    {
      id: 'm6',
      title: 'Donor Systems & Proposal Development',
      duration: '1 Month',
      description: 'Navigate the complex landscape of humanitarian funding and write compelling proposals.',
      topics: [
        'Overview of major humanitarian donors (USAID, ECHO, UN agencies)',
        'Understanding donor requirements and compliance',
        'Proposal writing techniques and best practices',
        'Budget narratives and cost allocation',
        'Grant management and donor reporting'
      ]
    },
    {
      id: 'm7',
      title: 'Protection, Gender & Inclusion',
      duration: '1 Month',
      description: 'Integrate protection principles and inclusive approaches across all humanitarian programming.',
      topics: [
        'Protection mainstreaming in humanitarian response',
        'Gender analysis and gender-responsive programming',
        'Child protection in emergencies',
        'Gender-based violence prevention and response',
        'Inclusion of persons with disabilities and marginalized groups'
      ]
    },
    {
      id: 'm8',
      title: 'Logistics & Operations in Humanitarian Response',
      duration: '1 Month',
      description: 'Master the operational backbone of humanitarian action: procurement, supply chain, and field operations.',
      topics: [
        'Humanitarian supply chain management',
        'Procurement principles and procedures',
        'Warehouse and inventory management',
        'Fleet management and transportation',
        'Security management and duty of care'
      ]
    },
    {
      id: 'm9',
      title: 'Leadership & Coordination in the Humanitarian System',
      duration: '1 Month',
      description: 'Develop leadership skills and learn to navigate complex coordination structures.',
      topics: [
        'Leadership styles and emotional intelligence',
        'The cluster system and humanitarian coordination',
        'Civil-military coordination',
        'Negotiation and advocacy skills',
        'Managing teams in high-stress environments'
      ]
    },
    {
      id: 'm10',
      title: 'Capstone Field Project',
      duration: '3 Months',
      description: 'Apply your learning to a real-world humanitarian challenge through a guided field project.',
      topics: [
        'Project identification and scoping',
        'Research methodology and data collection',
        'Stakeholder engagement and coordination',
        'Implementation and documentation',
        'Final presentation and peer review'
      ],
      isCapstone: true
    },
  ];

  const doctoralCourses = [
    {
      id: 'd1',
      title: 'Advanced Theories in Humanitarian Action',
      duration: '3 Months',
      year: 'Year 1',
      description: 'Deep exploration of theoretical frameworks underpinning humanitarian studies and their practical applications.',
      topics: [
        'Critical theories in humanitarianism',
        'Postcolonial perspectives on aid and development',
        'Political economy of humanitarian action',
        'Humanitarian ethics and moral philosophy',
        'Emerging paradigms in humanitarian thought'
      ]
    },
    {
      id: 'd2',
      title: 'Research Methods in Humanitarian Studies',
      duration: '3 Months',
      year: 'Year 1',
      description: 'Comprehensive training in research methodologies appropriate for humanitarian contexts.',
      topics: [
        'Qualitative research methods',
        'Quantitative research methods',
        'Mixed methods approaches',
        'Research in conflict and crisis settings',
        'Ethical considerations in humanitarian research'
      ]
    },
    {
      id: 'd3',
      title: 'Policy Analysis & Advocacy in Development',
      duration: '3 Months',
      year: 'Year 1',
      description: 'Analyze humanitarian policies and develop effective advocacy strategies for systemic change.',
      topics: [
        'Policy analysis frameworks',
        'Evidence-based policy making',
        'Advocacy strategies and campaigns',
        'Engaging with policy makers',
        'Measuring policy impact'
      ]
    },
    {
      id: 'd4',
      title: 'Advanced International Humanitarian Law',
      duration: '3 Months',
      year: 'Year 1',
      description: 'In-depth study of IHL and its application in contemporary conflicts and humanitarian crises.',
      topics: [
        'Evolution of IHL in modern conflicts',
        'Non-state armed groups and IHL',
        'Accountability mechanisms and tribunals',
        'Emerging challenges: cyber warfare, autonomous weapons',
        'IHL implementation and enforcement'
      ]
    },
    {
      id: 'd5',
      title: 'Strategic Leadership in Humanitarian Organizations',
      duration: '3 Months',
      year: 'Year 2',
      description: 'Develop executive-level leadership capabilities for humanitarian organizations.',
      topics: [
        'Strategic planning and organizational development',
        'Change management in humanitarian contexts',
        'Board governance and stakeholder relations',
        'Crisis leadership and decision-making',
        'Building organizational resilience'
      ]
    },
    {
      id: 'd6',
      title: 'Quantitative & Qualitative Research Design',
      duration: '3 Months',
      year: 'Year 2',
      description: 'Advanced course in designing rigorous research studies for doctoral dissertation.',
      topics: [
        'Advanced statistical analysis',
        'Longitudinal study design',
        'Comparative case study methods',
        'Grounded theory and phenomenology',
        'Data visualization and presentation'
      ]
    },
    {
      id: 'd7',
      title: 'Ethics in Humanitarian Research & Practice',
      duration: '3 Months',
      year: 'Year 2',
      description: 'Explore complex ethical dimensions of humanitarian work and research.',
      topics: [
        'Research ethics in vulnerable populations',
        'Institutional Review Board processes',
        'Ethical frameworks for humanitarian action',
        'Balancing competing ethical obligations',
        'Professional ethics and integrity'
      ]
    },
    {
      id: 'd8',
      title: 'Global Health & Humanitarian Response',
      duration: '3 Months',
      year: 'Year 2',
      description: 'Examine the intersection of global health and humanitarian action in crisis settings.',
      topics: [
        'Health systems in humanitarian emergencies',
        'Epidemic and pandemic response',
        'Mental health in humanitarian contexts',
        'Health equity and access',
        'One Health approaches'
      ]
    },
    {
      id: 'd9',
      title: 'Doctoral Dissertation',
      duration: '12 Months',
      year: 'Year 3',
      description: 'Original research contributing to humanitarian scholarship with faculty guidance.',
      topics: [
        'Dissertation proposal development',
        'Research methodology application',
        'Data collection and analysis',
        'Academic writing skills',
        'Faculty mentorship and feedback',
        'Final dissertation submission'
      ],
      isDissertation: true
    },
  ];

  const ppmCourses = [
    {
      id: 'ppm1',
      title: 'Development Project Management',
      duration: '1 Month',
      description: 'Master the fundamentals of managing development projects from initiation to closure.',
      topics: [
        'Project lifecycle and phases',
        'Project planning and scheduling',
        'Stakeholder management',
        'Risk management in development projects',
        'Project documentation and reporting'
      ]
    },
    {
      id: 'ppm2',
      title: 'Development Program Management',
      duration: '1 Month',
      description: 'Learn to coordinate and manage multiple related projects to achieve strategic development goals.',
      topics: [
        'Program vs project management',
        'Program governance and structure',
        'Benefits management and realization',
        'Cross-project coordination',
        'Strategic alignment of programs'
      ]
    },
    {
      id: 'ppm3',
      title: 'Development MEAL',
      duration: '1 Month',
      description: 'Develop comprehensive Monitoring, Evaluation, Accountability, and Learning frameworks.',
      topics: [
        'MEAL framework design',
        'Indicator development and data collection',
        'Evaluation methodologies',
        'Accountability mechanisms',
        'Learning and adaptive management'
      ]
    },
    {
      id: 'ppm4',
      title: 'Development Finance',
      duration: '1 Month',
      description: 'Understand financial management principles specific to development organizations.',
      topics: [
        'Budget development and management',
        'Donor compliance and reporting',
        'Cost-benefit analysis',
        'Financial sustainability',
        'Audit and financial controls'
      ]
    },
    {
      id: 'ppm5',
      title: 'Theory of Change (ToC)',
      duration: '1 Month',
      description: 'Learn to develop and apply Theory of Change for effective development interventions.',
      topics: [
        'Theory of Change for Development Projects and Programs'
      ]
    },
    {
      id: 'ppm6',
      title: 'Capstone Field Project',
      duration: '4 Months',
      description: 'Apply your learning through a comprehensive capstone project addressing real development challenges.',
      topics: [
        'Project identification and scoping',
        'Research methodology and data collection',
        'Stakeholder engagement and coordination',
        'Implementation and documentation',
        'Final presentation and peer review'
      ],
      isCapstone: true
    },
  ];

  const getActiveCourses = () => {
    switch (activeTab) {
      case 'masters':
        return mastersCourses;
      case 'ppm':
        return ppmCourses;
      case 'doctoral':
        return doctoralCourses;
      default:
        return mastersCourses;
    }
  };

  const activeCourses = getActiveCourses();

  const getQuickStats = () => {
    switch (activeTab) {
      case 'masters':
        return { courses: '9', months: '12', project: 'Capstone' };
      case 'ppm':
        return { courses: '5', months: '9', project: 'Capstone' };
      case 'doctoral':
        return { courses: '8', months: '36', project: 'Dissertation' };
      default:
        return { courses: '9', months: '12', project: 'Capstone' };
    }
  };

  const stats = getQuickStats();

  return (
    <div className="min-h-screen bg-white" data-testid="courses-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Curriculum
            </p>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Course Catalog
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Explore our comprehensive curriculum for all programs, 
              designed to develop humanitarian and development leaders at every stage of their career.
            </p>
          </motion.div>

          {/* Program Tabs */}
          <div className="flex flex-wrap gap-4 mt-12">
            <button
              onClick={() => setActiveTab('masters')}
              className={`px-6 py-3 font-medium rounded-sm transition-all ${
                activeTab === 'masters'
                  ? 'bg-[#163A59] text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="masters-tab"
            >
              Master's (Humanitarian)
            </button>
            <button
              onClick={() => setActiveTab('ppm')}
              className={`px-6 py-3 font-medium rounded-sm transition-all ${
                activeTab === 'ppm'
                  ? 'bg-[#C82032] text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="ppm-tab"
            >
              Master's (PPM)
            </button>
            <button
              onClick={() => setActiveTab('doctoral')}
              className={`px-6 py-3 font-medium rounded-sm transition-all ${
                activeTab === 'doctoral'
                  ? 'bg-[#163A59] text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="doctoral-tab"
            >
              Doctoral Program
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 mt-8">
            <div className="bg-white p-6 md:p-8 border border-[#DCE4EA]">
              <p className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-[#163A59]">
                {stats.courses}
              </p>
              <p className="text-sm text-slate-600 mt-1">Courses</p>
            </div>
            <div className="bg-white p-6 md:p-8 border border-[#DCE4EA]">
              <p className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-[#163A59]">
                {stats.months}
              </p>
              <p className="text-sm text-slate-600 mt-1">Months</p>
            </div>
            <div className="bg-white p-6 md:p-8 border border-[#DCE4EA]">
              <p className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-[#163A59]">1</p>
              <p className="text-sm text-slate-600 mt-1">
                {stats.project}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Courses List */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-6">
            {activeCourses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={`bg-white border ${course.isCapstone || course.isDissertation ? 'border-[#C82032]/30' : 'border-[#DCE4EA]'} hover:shadow-lg transition-all`}
              >
                <button
                  onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                  className="w-full text-left p-6 md:p-8"
                  data-testid={`course-${course.id}-toggle`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 md:gap-6">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-sm font-semibold ${course.isCapstone || course.isDissertation ? 'bg-[#C82032] text-white' : 'bg-[#163A59]/10 text-[#163A59]'}`}>
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-['Source_Serif_4'] text-xl md:text-2xl font-semibold text-slate-900 mb-2">
                          {course.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {course.duration}
                          </span>
                          {course.year && (
                            <span className="px-2 py-1 bg-[#163A59]/10 text-[#163A59] text-xs font-medium rounded">
                              {course.year}
                            </span>
                          )}
                          {(course.isCapstone || course.isDissertation) && (
                            <span className="px-2 py-1 bg-[#C82032]/10 text-[#C82032] text-xs font-medium rounded">
                              {course.isCapstone ? 'Capstone' : 'Dissertation'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-slate-400">
                      {expandedCourse === course.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </div>
                  </div>
                </button>

                {expandedCourse === course.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="px-6 md:px-8 pb-6 md:pb-8 border-t border-[#DCE4EA]"
                  >
                    <div className="pt-6 md:pt-8 pl-0 md:pl-18">
                      <p className="text-slate-600 mb-6">{course.description}</p>
                      <h4 className="font-semibold text-slate-900 mb-4">Key Topics:</h4>
                      <ul className="space-y-3">
                        {course.topics.map((topic) => (
                          <li key={`topic-${topic.slice(0, 15)}`} className="flex items-start gap-3 text-slate-600">
                            <BookOpen size={16} className="text-[#163A59] mt-1 flex-shrink-0" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10">
              Apply now and take advantage of our 90% scholarship program 
              for eligible Afro-Asia students.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/admissions"
                className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-10 py-4 rounded-sm font-medium transition-all"
                data-testid="courses-apply-btn"
              >
                Apply Now
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/scholarship"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-10 py-4 rounded-sm font-medium transition-all"
                data-testid="courses-scholarship-btn"
              >
                Learn About Scholarship
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Courses;
